import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { capitalize } from '../../../../utils/functions'
import { CourseAnalyticState } from '../../../../features/types'

interface Props {
    analyticsData: CourseAnalyticState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const AnalyticsTable: React.FC<Props> = ({
    analyticsData = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage,
    isFetching,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage, locale } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, rowsPerPage - analyticsData.length) : 0

    // const emptyRows =
    // page > 0 ? Math.max(0, (1 + page) * rowsPerPage - teachersData.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'course' })}
                            </TableCell>

                            <TableCell>
                                Жалпы оқып жатқандар саны
                            </TableCell>

                            <TableCell>
                                Қазақша оқып жатқандар саны
                            </TableCell>

                            <TableCell>
                                Орысша оқып жатқандар саны
                            </TableCell>

                            <TableCell>
                                Пәндер аналитикасы
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {analyticsData?.length ? (
                        <TableBody>
                            {analyticsData?.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.[`courseTitle${capitalize(locale)}`]}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.totalCount}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.totalCountKz}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row?.totalCountRu
                                        }
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="center"
                                    >
                                       <Link
                                            to={`${row?.id}`}
                                            state={{
                                                course: row,
                                            }}
                                        >
                                            <IconButton sx={{ background: '#f3f4f5' }}>
                                                <img
                                                    src={ArrowRightIcon}
                                                    alt="details"
                                                />
                                            </IconButton>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={10} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={10}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default AnalyticsTable
